import {
	Connection,
	PublicKey,
	SystemProgram,
	Transaction,
	VersionedTransaction,
} from "@solana/web3.js";
import {
	TOKEN_PROGRAM_ID,
	createTransferInstruction,
	getAssociatedTokenAddressSync,
} from "@solana/spl-token";
import { SOL_ADDRESS, solToLamports, tokenAmountToRaw } from "./utils";

const RPC_URL =
	import.meta.env.VITE_SOLANA_RPC_URL ?? "https://api.mainnet-beta.solana.com";

export function getConnection(): Connection {
	return new Connection(RPC_URL, "confirmed");
}

export interface SimulatedSolanaFee {
	feeLamports: number;
	unitsConsumed?: number;
	simulationError?: unknown;
}

function isVersionedTransaction(
	tx: Transaction | VersionedTransaction,
): tx is VersionedTransaction {
	return "message" in tx;
}

function getTransactionMessage(tx: Transaction | VersionedTransaction) {
	return isVersionedTransaction(tx) ? tx.message : tx.compileMessage();
}

function toSimulationTransaction(tx: Transaction | VersionedTransaction): VersionedTransaction {
	return isVersionedTransaction(tx) ? tx : new VersionedTransaction(tx.compileMessage());
}

export async function simulateSolanaFee(
	tx: Transaction | VersionedTransaction,
): Promise<SimulatedSolanaFee> {
	const connection = getConnection();
	const message = getTransactionMessage(tx);

	const feeResult = await connection.getFeeForMessage(message, "confirmed");
	if (feeResult.value === null) {
		throw new Error("Unable to calculate Solana network fee for this transaction.");
	}

	const simulation = await connection.simulateTransaction(toSimulationTransaction(tx), {
		commitment: "confirmed",
		replaceRecentBlockhash: false,
		sigVerify: false,
	});

	return {
		feeLamports: feeResult.value,
		simulationError: simulation.value.err,
		unitsConsumed: simulation.value.unitsConsumed,
	};
}

export async function buildSolTransferTx(
	fromAddress: string,
	toAddress: string,
	amountSol: number,
	feePayer: string,
): Promise<Transaction> {
	const connection = getConnection();
	const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash();

	const tx = new Transaction();
	tx.recentBlockhash = blockhash;
	tx.lastValidBlockHeight = lastValidBlockHeight;
	tx.feePayer = new PublicKey(feePayer);

	tx.add(
		SystemProgram.transfer({
			fromPubkey: new PublicKey(fromAddress),
			toPubkey: new PublicKey(toAddress),
			lamports: solToLamports(amountSol),
		}),
	);

	return tx;
}

export async function buildSplTransferTx(
	fromAddress: string,
	toAddress: string,
	mintAddress: string,
	amount: number,
	decimals: number,
	feePayer: string,
): Promise<Transaction> {
	const connection = getConnection();
	const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash();

	const fromPubkey = new PublicKey(fromAddress);
	const toPubkey = new PublicKey(toAddress);
	const mintPubkey = new PublicKey(mintAddress);
	const feePayerPubkey = new PublicKey(feePayer);

	const fromTokenAccount = getAssociatedTokenAddressSync(mintPubkey, fromPubkey);
	const toTokenAccount = getAssociatedTokenAddressSync(mintPubkey, toPubkey);

	const tx = new Transaction();
	tx.recentBlockhash = blockhash;
	tx.lastValidBlockHeight = lastValidBlockHeight;
	tx.feePayer = feePayerPubkey;

	const toAccountInfo = await connection.getAccountInfo(toTokenAccount);
	if (!toAccountInfo) {
		const { createAssociatedTokenAccountInstruction } = await import("@solana/spl-token");
		tx.add(
			createAssociatedTokenAccountInstruction(
				feePayerPubkey,
				toTokenAccount,
				toPubkey,
				mintPubkey,
			),
		);
	}

	tx.add(
		createTransferInstruction(
			fromTokenAccount,
			toTokenAccount,
			fromPubkey,
			tokenAmountToRaw(amount, decimals),
			[],
			TOKEN_PROGRAM_ID,
		),
	);

	return tx;
}

export async function buildSendTransaction(
	fromAddress: string,
	toAddress: string,
	tokenAddress: string,
	amount: number,
	decimals: number,
	feePayer: string,
): Promise<Transaction> {
	if (tokenAddress === SOL_ADDRESS) {
		return buildSolTransferTx(fromAddress, toAddress, amount, feePayer);
	}
	return buildSplTransferTx(fromAddress, toAddress, tokenAddress, amount, decimals, feePayer);
}

// Safe base64 that works even when Buffer polyfill is absent.
// btoa(String.fromCharCode(...)) is native in all browsers.
function uint8ToBase64(bytes: Uint8Array): string {
	let binary = "";
	for (let i = 0; i < bytes.length; i++) {
		binary += String.fromCharCode(bytes[i]!);
	}
	return btoa(binary);
}

// Serialize any transaction to base64 without relying on instanceof.
// Dynamic SDK bundles its own @solana/web3.js so instanceof checks against
// our copy fail — instead we probe the shape of the serialize() return value.
export function serializeTransaction(tx: Transaction | VersionedTransaction): string {
	// VersionedTransaction.serialize() takes no arguments and returns Uint8Array.
	// Legacy Transaction.serialize() accepts options and also returns a Buffer/Uint8Array.
	// OBLIQ signs the fee payer after the user signs, so legacy transactions must be
	// allowed through with the OBLIQ payer signature still missing.
	// biome-ignore lint/suspicious/noExplicitAny: cross-package type boundary
	const raw = (tx as any).serialize({
		requireAllSignatures: false,
		verifySignatures: true,
	}) as Uint8Array | Buffer;
	const bytes = raw instanceof Uint8Array ? raw : new Uint8Array(raw);
	return uint8ToBase64(bytes);
}


export function bytesToBase64(bytes: Uint8Array): string {
	return uint8ToBase64(bytes);
}

export function deserializeVersionedTx(base64: string): VersionedTransaction {
	// atob is native in browsers; no Buffer dependency needed
	const binary = atob(base64);
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i++) {
		bytes[i] = binary.charCodeAt(i);
	}
	return VersionedTransaction.deserialize(bytes);
}
